import React, { useEffect } from 'react';
import { AnalyticsSenderProvider, useAnalytics } from '@funtech-int/yango-shared';
import { useRouteMatch, useRouter, useSignal } from '@funtech-int/rival-react';

import { clientOnlyPage } from '@/shared/lib/clientOnly';
import { useSportEventQuery } from '@/shared/hooks/useSportEventQuery';
import { useTimeSignal } from '@/shared/hooks/useTimeSignal';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { PlusPaymentPlace } from '@/shared/constants/payments';
import { PlayerManager } from '@/scenarios/legacy-player/components/PlayerManager/PlayerManagerMain';

import { sportEventRoute } from '../../routes';
import { EventContent } from '../EventContent/EventContent';
import { PromoCover } from '../PromoCover/PromoCover';
import {
    EventContentAction,
    EventContentActionValue,
} from '../EventContentAction/EventContentAction';
import { createSportEventScreenAnalytics } from '../../lib/analytics';

import styles from './EventScreen.module.css';

const TIME_UPDATE_INTERVAL = 60 * 1000;
const PERMITTED_ACTIONS = new Set<EventContentActionValue>(['Login', 'Watch', 'Offer']);

const EventComponent = () => {
    const router = useRouter();
    const { params } = useRouteMatch(sportEventRoute);
    const { sportEvent } = useSportEventQuery({ id: params.id });
    const analytics = useAnalytics((eventSender) =>
        createSportEventScreenAnalytics(eventSender, sportEvent)
    );
    const timeSignal = useTimeSignal(TIME_UPDATE_INTERVAL);
    const currentTimestamp = useSignal(timeSignal);
    const loginUrl = useLoginUrl(new URL(window.location.href));

    useEffect(() => {
        analytics.pageShowed();
    }, [analytics]);

    const itemEventState = sportEvent.getState(currentTimestamp);
    const { coverUrl } = sportEvent.getAssets();

    const onWatch = () => {
        startPlaying({
            router,
            contentId: sportEvent.id,
            contentType: PlayerContentType.SPORT_LIVE,
            isNewPlayerEnabled: false,
            additionalQueryParams: {
                sportEventId: sportEvent.sportEventId,
            },
        });
    };

    return (
        <main className={styles.root}>
            <PromoCover coverUrl={coverUrl} alt={sportEvent.title} contentKey={sportEvent.id} />
            <div className={styles.contentWrapper}>
                <EventContent
                    item={sportEvent}
                    eventState={itemEventState}
                    contentAction={
                        <EventContentAction
                            permittedActions={PERMITTED_ACTIONS}
                            viewOption={sportEvent.viewOption}
                            eventState={itemEventState}
                            plusPaymentPlace={PlusPaymentPlace.WatchButton}
                            loginUrl={loginUrl}
                            onWatch={onWatch}
                        />
                    }
                />
            </div>
        </main>
    );
};

const EventScreen = clientOnlyPage(() => (
    <>
        <PlayerManager />
        <AnalyticsSenderProvider>
            <EventComponent />
        </AnalyticsSenderProvider>
    </>
));

export default EventScreen;
